import { onMounted, onUnmounted, Ref } from 'vue';

type ArrowKeyNavigationOptions = {
  root: Ref<HTMLElement | null>;
  dropdownOpened?: Ref<boolean | undefined>;
  selector?: string;
};

const DEFAULT_SELECTOR =
  '.dropdown-item, .num-selector__button:not(.num-selector__button--disabled), button.button-primary, .icon-button--close';

// method for handling keyboard navigation on dropdowns like terminal selector or customer selector
// Up/Down arrow to cycle through available options - when cycling past the last option it will loop back to the first
// Enter/Space to confirm selection
export function useArrowKeyNavigation(options: ArrowKeyNavigationOptions) {
  const { root, dropdownOpened, selector = DEFAULT_SELECTOR } = options;

  const handleArrowKeysOnDropdowns = (event: KeyboardEvent): void => {
    const rootEl = root.value;
    if (!rootEl) return;

    if (!['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Tab'].includes(event.key)) return;
    if (!rootEl.contains(event.target as Node)) return;
    if (dropdownOpened?.value !== undefined && !dropdownOpened.value) return;

    const target = event.target as HTMLElement;
    const isInputElement = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    // for allowing normal space behaviour in input and text areas
    if (event.key === ' ' && isInputElement) return;

    const focusableItems = Array.from(rootEl.querySelectorAll<HTMLElement>(selector));

    if (!focusableItems.length) return;

    const activeIndex = focusableItems.indexOf(document.activeElement as HTMLElement);
    let nextIndex = activeIndex;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (activeIndex !== -1) focusableItems[activeIndex].click();
      return;
    }

    event.preventDefault();

    if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
      nextIndex = (activeIndex + 1) % focusableItems.length;
    } else if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
      nextIndex = (activeIndex - 1 + focusableItems.length) % focusableItems.length;
    }

    focusableItems[nextIndex].focus();
  };

  onMounted(() => {
    document.addEventListener('keydown', handleArrowKeysOnDropdowns as EventListener);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleArrowKeysOnDropdowns as EventListener);
  });

  return {
    handleArrowKeysOnDropdowns,
  };
}
